import isBlank from '@sedan-utils/is-blank';

import { BasicColumnTypes, IOgqlColumn, IOgqlSemanticType } from '@opengov/ogql-types';

import { IColumnAlias } from './reportConfiguration';
import { IDatasetLensColumn } from './reportDataset';
import { DateColumnName } from './reportColumns';

import { createRecord, DeepPartial } from './record';
import { OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME } from './constants';
import { IColumnFilter } from './reportFilters';

export const ORGANIZED_BY_COLUMN_ALIAS = 'org__by_og';
export const CATEGORIZED_BY_COLUMN_ALIAS = 'cat_e_gory_og';
export const AGGREGATE = 'Aggregate';

export enum EColumnDimensionType {
  CHART_OF_ACCOUNTS = 'opengov/chart-of-accounts',
  DATE = 'opengov/date',
  FISCAL_YEAR_FISCAL_PERIOD = 'opengov/fiscalyearperiod'
}

export enum EReportColumnTypes {
  dimensional = 'dimensional',
  storage = 'storage',
  infix = 'infix', // aka calculated
  conditional = 'conditional',
  percentOfTotal = 'percentOfTotal',
  constant = 'constant',
  aggregate = 'aggregate',
  targetStatus = 'targetStatus'
}

export interface IPercentOfTotalReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.percentOfTotal;
  excludedCategorizedByValues?: string[];
  originalColumnType: EReportColumnTypes;
  // if original column type was infix
  operator?: EOgqlInfixColumnOperators;
  leftColumn?: IReportColumn;
  rightColumn?: IReportColumn;
  // if original column type was conditional
  sourceColumn?: IReportColumn;
  conditions?: IConditionalExpressionCondition<any>[];
  else?: IReportColumn;
}

export enum EOgqlInfixColumnOperators {
  add = '+',
  divide = '/',
  multiply = '*',
  subtract = '-'
}

export enum EOgqlAggregateColumnOperators {
  count = 'count',
  sum = 'sum'
}

export enum EConditionalComparisonOperators {
  greaterThan = 'greaterThan',
  greaterThanOrEqualTo = 'greaterThanOrEqualTo',
  lessThan = 'lessThan',
  lessThanOrEqualTo = 'lessThanOrEqualTo',
  equalTo = 'equalTo',
  notEqualTo = 'notEqualTo',
  between = 'between' // inclusive between e.g. `10 <= x <= 20`
}

export enum EReportConstant {
  Day = 'day',
  Month = 'month',
  Week = 'week',
  Quarter = 'quarter',
  Year = 'year',
  Number = 'number',
  Percentage = 'percentage',
  Date = 'date',
  Text = 'text'
}

export interface IConstantReportColumn<ValueType extends string | number | Date> extends IBaseReportColumn {
  columnType: EReportColumnTypes.constant;
  value: ValueType;
  unit: EReportConstant;
}

export interface IStorageReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.storage;
}

export interface IDimensionalReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.dimensional;
  dimensionType: EColumnDimensionType;
  dimensionId: string;
  joinColumnName?: string;
  accountCodeColumnRef?: string; // for the query and summary table
  descriptionColumnRef: string; // for the query and summary table
}

export interface IDimensionalDateReportColumn extends IDimensionalReportColumn {
  dateColumnName: DateColumnName | string;
}

export interface IInfixReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.infix;
  operator: EOgqlInfixColumnOperators;
  leftColumn: IReportColumn; // better names
  rightColumn: IReportColumn;
}

export interface IConditionalReportColumn<OutputColumnType extends string | number | Date> extends IBaseReportColumn {
  columnType: EReportColumnTypes.conditional;
  sourceColumn: IReportColumn;
  conditions: IConditionalExpressionCondition<OutputColumnType>[];
  else: OutputColumnType;
}

export interface ITargetStatusReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.targetStatus;
}

// IConditionalExpressionComparisonValue is the value(s) we are comparing against
export type IConditionalExpressionComparisonValue<OutputColumnType extends string | number | Date> = OutputColumnType;

export type IConditionalExpressionCondition<OutputColumnType extends string | number | Date> = {
  operator: EConditionalComparisonOperators;
  lowerBound?: IConditionalExpressionComparisonValue<any>;
  upperBound?: IConditionalExpressionComparisonValue<any>;
  matchValue?: IConditionalExpressionComparisonValue<any>;
  returnedValue: OutputColumnType;
};

export const createNewCondition = (
  values: DeepPartial<IConditionalExpressionCondition<any>> = {}
): IConditionalExpressionCondition<any> => ({
  operator: null,
  matchValue: null,
  returnedValue: null,
  ...values
});

export const createNewConditionBetween = (
  values: DeepPartial<IConditionalExpressionCondition<any>> = {}
): IConditionalExpressionCondition<any> => ({
  operator: null,
  lowerBound: null,
  upperBound: null,
  returnedValue: null,
  ...values
});

export interface IAggregateReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.aggregate;
  name: EOgqlAggregateColumnOperators;
  aggregateType: EOgqlAggregateColumnOperators;
}

export type IReportColumnMetaDataTags = {
  hidden?: boolean;
  units?: string;
};

export type IReportColumnMetaDataFacts = {
  distinctCount?: string; // OGQL returns strings even for numeric values
  minimum?: string;
  maximum?: string;
};

export const createReportColumnMetaDataTags = (
  values: Partial<IReportColumnMetaDataTags> = {}
): IReportColumnMetaDataTags => ({
  hidden: null,
  units: null,
  ...values
});

export const createReportColumnMetaDataFacts = (
  values: Partial<IReportColumnMetaDataFacts> = {}
): IReportColumnMetaDataFacts => ({
  distinctCount: null,
  minimum: null,
  maximum: null,
  ...values
});

export interface IReportColumnMetadata {
  tags: IReportColumnMetaDataTags;
  facts: IReportColumnMetaDataFacts;
  units?: string;
}

// this one needs to ignore semanticTypes.attributes
export const createOgqlSemanticType = (values: Partial<IOgqlSemanticType> = {}): IOgqlSemanticType => ({
  type: isBlank(values.type) ? null : values.type,
  name: isBlank(values.name) ? null : values.name
});

const ogqlSemanticTypeOrString = (rs: IOgqlSemanticType | string) => {
  if ((rs as IOgqlSemanticType).type) {
    return createOgqlSemanticType(rs as IOgqlSemanticType);
  }

  return rs;
};

export const createReportColumnMetaData = (values: Partial<IReportColumnMetadata> = {}): IReportColumnMetadata => ({
  units: null,
  ...values,
  tags: values.tags ? createReportColumnMetaDataTags(values.tags) : createReportColumnMetaDataTags(),
  facts: values.facts ? createReportColumnMetaDataFacts(values.facts) : createReportColumnMetaDataFacts()
});
export interface IBaseReportColumn {
  name: string;
  displayName: string;
  originalDisplayName?: string;
  type?: BasicColumnTypes;
  id?: string;
  active?: boolean;
  hidden?: boolean;
  // Delphius produces IOgqlSemanticType where lenses produce string
  semanticTypes?: (IOgqlSemanticType | string)[];
  metadata?: IReportColumnMetadata;
}

export type IReportColumn =
  | IDimensionalReportColumn
  | IStorageReportColumn
  | IConditionalReportColumn<any>
  | IInfixReportColumn
  | IPercentOfTotalReportColumn
  | IConstantReportColumn<any>
  | IAggregateReportColumn
  | ITargetStatusReportColumn;

const storageReportColumnRecord = createRecord<IStorageReportColumn>({
  displayName: '',
  hidden: false,
  name: '',
  type: BasicColumnTypes.Text,
  semanticTypes: [],
  metadata: createReportColumnMetaData(),
  columnType: EReportColumnTypes.storage
});

export const createStorageReportColumn = (values: DeepPartial<IStorageReportColumn> = {}): IStorageReportColumn => ({
  ...storageReportColumnRecord(values),
  semanticTypes: values.semanticTypes ? values.semanticTypes.map(ogqlSemanticTypeOrString) : [],
  metadata: createReportColumnMetaData(values.metadata),
  columnType: EReportColumnTypes.storage
});

export const createDimensionalReportColumn = (
  values: DeepPartial<IDimensionalReportColumn> = {}
): IDimensionalReportColumn => ({
  accountCodeColumnRef: null,
  descriptionColumnRef: null,
  dimensionId: null,
  dimensionType: null,
  displayName: '',
  hidden: false,
  name: '',
  type: BasicColumnTypes.Text,
  joinColumnName: null,
  ...values,
  semanticTypes: values.semanticTypes ? values.semanticTypes.map(ogqlSemanticTypeOrString) : [],
  columnType: EReportColumnTypes.dimensional,
  metadata: values.metadata ? createReportColumnMetaData(values.metadata) : createReportColumnMetaData()
});

export const createDimensionalDateReportColumn = (
  values: DeepPartial<IDimensionalDateReportColumn> = {}
): IDimensionalDateReportColumn => ({
  accountCodeColumnRef: null,
  descriptionColumnRef: null,
  dateColumnName: '',
  dimensionId: null,
  displayName: '',
  hidden: false,
  name: '',
  dimensionType: EColumnDimensionType.DATE,
  type: BasicColumnTypes.Text,
  ...values,
  semanticTypes: values.semanticTypes ? values.semanticTypes.map(ogqlSemanticTypeOrString) : [],
  columnType: EReportColumnTypes.dimensional,
  metadata: values.metadata ? createReportColumnMetaData(values.metadata) : createReportColumnMetaData()
});

// for some unknown historical reason the infix Record does not have semanticType nor metaData
// even tho the type has them
const infixReportColumnRecord = createRecord<IInfixReportColumn>({
  columnType: EReportColumnTypes.infix,
  displayName: '',
  hidden: false,
  leftColumn: createStorageReportColumn(),
  name: '',
  operator: null,
  rightColumn: createStorageReportColumn(),
  type: BasicColumnTypes.Text
});

export const createInfixReportColumn = (values: DeepPartial<IInfixReportColumn> = {}): IInfixReportColumn => ({
  ...infixReportColumnRecord(values),
  columnType: EReportColumnTypes.infix,
  leftColumn: values.leftColumn ? mapColumnToFactory(values.leftColumn) : createStorageReportColumn(),
  rightColumn: values.rightColumn ? mapColumnToFactory(values.rightColumn) : createStorageReportColumn()
});

const percentOfTotalReportColumnRecord = createRecord<IPercentOfTotalReportColumn>({
  columnType: EReportColumnTypes.percentOfTotal,
  conditions: null,
  displayName: '',
  else: null,
  excludedCategorizedByValues: [],
  hidden: false,
  leftColumn: null,
  name: '',
  operator: null,
  originalColumnType: EReportColumnTypes.storage,
  rightColumn: null,
  sourceColumn: null,
  type: BasicColumnTypes.Text
});

export const createPercentOfTotalReportColumn = (
  values: DeepPartial<IPercentOfTotalReportColumn> = {}
): IPercentOfTotalReportColumn => ({
  ...percentOfTotalReportColumnRecord(values),
  columnType: EReportColumnTypes.percentOfTotal
});

const constantReportColumnRecord = createRecord<IConstantReportColumn<any>>({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.constant,
  hidden: false,
  value: null,
  unit: EReportConstant.Number
});

export const createConstantReportColumn = (
  values: DeepPartial<IConstantReportColumn<any>> = {}
): IConstantReportColumn<any> => ({
  ...constantReportColumnRecord(values),
  columnType: EReportColumnTypes.constant
});

const getListOfConditions = (conditions: IConditionalExpressionCondition<any>[] = []) => {
  const conditionList = [];
  conditions.forEach((condition) => {
    condition.operator === EConditionalComparisonOperators.between
      ? conditionList.push(createNewConditionBetween(condition))
      : conditionList.push(createNewCondition(condition));
  });

  return conditionList;
};

const conditionalReportColumnRecord = createRecord<IConditionalReportColumn<any>>({
  columnType: EReportColumnTypes.conditional,
  conditions: [],
  displayName: '',
  else: '',
  hidden: false,
  metadata: createReportColumnMetaData(),
  name: '',
  sourceColumn: createStorageReportColumn(),
  type: BasicColumnTypes.Text
});

export const createConditionalReportColumn = (
  values: DeepPartial<IConditionalReportColumn<any>> = {}
): IConditionalReportColumn<any> => ({
  ...conditionalReportColumnRecord(values),
  columnType: EReportColumnTypes.conditional,
  conditions: values.conditions
    ? getListOfConditions(values.conditions as unknown as IConditionalExpressionComparisonValue<any>)
    : [],
  metadata: values.metadata ? createReportColumnMetaData(values.metadata) : createReportColumnMetaData(),
  sourceColumn: values.sourceColumn ? mapColumnToFactory(values.sourceColumn) : createStorageReportColumn()
});

const aggregateReportColumnRecord = createRecord<IAggregateReportColumn>({
  name: EOgqlAggregateColumnOperators.count,
  aggregateType: EOgqlAggregateColumnOperators.count,
  displayName: 'Count',
  type: BasicColumnTypes.Numeric,
  columnType: EReportColumnTypes.aggregate,
  hidden: false
});

export const createAggregateReportColumn = (
  values: DeepPartial<IAggregateReportColumn> = {}
): IAggregateReportColumn => ({
  ...aggregateReportColumnRecord(values),
  columnType: EReportColumnTypes.aggregate
});

const targetStatusReportColumnRecord = createRecord<ITargetStatusReportColumn>({
  columnType: EReportColumnTypes.targetStatus,
  displayName: 'Target Status',
  hidden: false,
  name: EReportColumnTypes.targetStatus,
  type: BasicColumnTypes.Numeric
});

export const createTargetStatusReportColumn = (
  values: DeepPartial<ITargetStatusReportColumn> = {}
): ITargetStatusReportColumn => ({
  ...targetStatusReportColumnRecord(values),
  columnType: EReportColumnTypes.targetStatus
});

export const categorizedByNoneColumnObject: IAggregateReportColumn = createAggregateReportColumn({
  aggregateType: EOgqlAggregateColumnOperators.sum,
  columnType: EReportColumnTypes.aggregate,
  displayName: AGGREGATE,
  hidden: false,
  name: EOgqlAggregateColumnOperators.sum,
  type: BasicColumnTypes.Numeric
});

export const targetStatusReportColumnObject = createTargetStatusReportColumn();

export const mapColumnToFactory = (column: DeepPartial<IReportColumn>): IReportColumn => {
  if (!column) return;
  switch (column.columnType) {
    case EReportColumnTypes.dimensional:
      if ('dateColumnName' in column) {
        const dateColumn = column as DeepPartial<IDimensionalDateReportColumn>;
        return createDimensionalDateReportColumn(dateColumn);
      } else {
        return createDimensionalReportColumn(column);
      }
    case EReportColumnTypes.infix:
      return createInfixReportColumn(column);
    case EReportColumnTypes.conditional:
      return createConditionalReportColumn(column);
    case EReportColumnTypes.constant:
      return createConstantReportColumn(column);
    case EReportColumnTypes.percentOfTotal:
      return createPercentOfTotalReportColumn(column);
    case EReportColumnTypes.aggregate:
      return createAggregateReportColumn(column);
    case EReportColumnTypes.targetStatus:
      return createTargetStatusReportColumn(column);
    case EReportColumnTypes.storage:
    default:
      if (column.type && column.type.includes('logical/bool')) {
        return createStorageReportColumn({
          ...column,
          type: BasicColumnTypes.Boolean
        });
      }
      return createStorageReportColumn(column);
  }
};

export const getDisplayName = (
  column: IReportColumn | IDimensionalReportColumn,
  columnNames: IColumnAlias[],
  dataMgrColumn?: IOgqlColumn | IDatasetLensColumn
): string => {
  const customSettingsForColumn =
    (columnNames.find && columnNames.find((r) => r.originalName === column.name)) ||
    (columnNames.find && columnNames.find((r) => r.originalName === getColumnName(column.name)));
  if (customSettingsForColumn) {
    return customSettingsForColumn.displayName;
  }
  if (dataMgrColumn) {
    if ('label' in dataMgrColumn) {
      return dataMgrColumn.label;
    } else {
      return dataMgrColumn.displayName;
    }
  }
  return column.displayName;
};

export const getColumnName = (columnName: string): string => {
  if (!columnName) {
    return;
  }
  return columnName.split(':').pop();
};

export const getColumnWithSimpleName = (col: IReportColumn): IReportColumn => {
  if (!col) {
    return;
  }

  const column = JSON.parse(JSON.stringify(col));

  if (column.columnType === EReportColumnTypes.infix) {
    column.name = getColumnName(column.name);
    column.leftColumn = getColumnWithSimpleName(mapColumnToFactory(column.leftColumn));
    column.rightColumn = getColumnWithSimpleName(mapColumnToFactory(column.rightColumn));
    return column;
  } else {
    column.name = getColumnName(column.name);
    return column;
  }
};

export const getColumnByNameFromList = (columnName: string, columns: IReportColumn[]): IReportColumn => {
  const columnMatchOnCompoundName = columns.find((column) => column.name === columnName);
  if (columnMatchOnCompoundName) {
    return columnMatchOnCompoundName;
  } else {
    return (
      // if `columns` has non-compound names
      columns.find((column) => column.name === getColumnName(columnName)) ||
      // if `columnName` is compound and `columns` has compound names
      columns.find((column) => getColumnName(column.name) === getColumnName(columnName))
    );
  }
};

export function isFYFPColumn(column: IReportColumn) {
  return (
    column.columnType === EReportColumnTypes.dimensional &&
    column.joinColumnName === OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME
  );
}

export function isFYFPColumnFilter(columnFilter: IColumnFilter) {
  return columnFilter.columnName === OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME;
}
